export type ConnectionId = string & { readonly __brand: 'ConnectionId' };
export type ConnectionExternalId = string & { readonly __brand: 'ConnectionExternalId' };

/**
 * The one declaration of the terminus vocabulary. `pages/settings/inventory-sources/enums.js` aliases
 * these values for the legacy pages rather than restating them.
 */
export const TERMINUS_TYPE = {
  none: 'None',
  source: 'Source',
  target: 'Target',
} as const;

export type TerminusType = (typeof TERMINUS_TYPE)[keyof typeof TERMINUS_TYPE];

/** `connections[].status`, PascalCased by the .NET layer like every other enum on the envelope. */
export const CONNECTION_STATUS = {
  active: 'Active',
  inactive: 'Inactive',
  disabled: 'Disabled',
} as const;

export type ConnectionStatus = (typeof CONNECTION_STATUS)[keyof typeof CONNECTION_STATUS];

/**
 * The connection statuses under which a connection can participate in sync. `Disabled` is absent.
 * Values match `CONNECTION_STATUS` — compare directly with `connection.status`, no `.toLowerCase()`.
 * Legacy code in `pages/settings/inventory-sources/` has its own `InventoryConnectionValidStatusEnum`
 * with lowercase values and must keep calling `.toLowerCase()` before comparing.
 */
export const USABLE_CONNECTION_STATUSES = {
  active: CONNECTION_STATUS.active,
  inactive: CONNECTION_STATUS.inactive,
} as const;

/**
 * Job status as it appears inside the JSON-encoded `lastJobInfo` blob — lowercase, straight out of the
 * NestJS `jobs` table. All six states are listed: `new` and `killed` are missing from the legacy
 * `pages/settings/inventory-sources/enums.js`, and `killed` is reachable in production because the
 * scheduler's `killHungJobs` task runs every ten minutes.
 */
export const JOB_STATUS = {
  new: 'new',
  enqueued: 'enqueued',
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
  killed: 'killed',
} as const;

export type JobStatus = (typeof JOB_STATUS)[keyof typeof JOB_STATUS];

/**
 * The same six states as `JOB_STATUS`, spelled the way the top-level `lastJob` envelope sends them.
 * Both spellings are live at once and neither can be dropped: the .NET layer PascalCases what it puts
 * on the envelope, but re-serialises the `lastJobInfo` blob verbatim.
 */
export const ENVELOPE_JOB_STATUS = {
  new: 'New',
  enqueued: 'Enqueued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  killed: 'Killed',
} as const;

export type EnvelopeJobStatus = (typeof ENVELOPE_JOB_STATUS)[keyof typeof ENVELOPE_JOB_STATUS];

/** Job type inside the `lastJobInfo` blob. Lowercase, for the reason `JOB_STATUS` is lowercase. */
export const JOB_TYPE = {
  fetch: 'fetch',
  push: 'push',
} as const;

export type JobType = (typeof JOB_TYPE)[keyof typeof JOB_TYPE];

/** Job type on the top-level envelope. PascalCase, for the reason `ENVELOPE_JOB_STATUS` is. */
export const ENVELOPE_JOB_TYPE = {
  fetch: 'Fetch',
  push: 'Push',
} as const;

export type EnvelopeJobType = (typeof ENVELOPE_JOB_TYPE)[keyof typeof ENVELOPE_JOB_TYPE];

type SkuJobResultEntry = {
  sku: string;
  errorMessage?: string;
  totalAvailable?: number;
  [key: string]: unknown;
};

export type JobResult = {
  error_message?: string;
  error_status_code?: number;
  has_more_data?: boolean;
  skus_imported?: number;
  total_skus_updated?: number;
  total_skus_with_errors?: number;
  skus_with_errors?: SkuJobResultEntry[];
  pushed_skus?: SkuJobResultEntry[];
  syncPerformed?: boolean;
};

export type LastJobInfo = {
  createdAt: string;
  jobType: JobType;
  status: JobStatus;
  result?: JobResult;
};

type SyncAllToWarehouseConfig = {
  marketplace_warehouse_id: string;
  enabled: boolean;
};

/**
 * Backed by the `connections.config` jsonb column, which is neither typed nor validated
 * server-side — `PATCH /connections/:id` shallow-merges whatever it is sent. The index signature is
 * therefore real rather than defensive, and every write has to round-trip keys it does not own.
 */
export type ConnectionConfig = {
  min_sellable_quantity_enabled?: boolean;
  min_sellable_quantity?: number;
  max_sellable_quantity_enabled?: boolean;
  max_sellable_quantity?: number;
  buffer_stock_quantity_enabled?: boolean;
  buffer_stock_quantity?: number;
  sync_percentage_quantity_enabled?: boolean;
  sync_percentage_quantity?: number;
  external_warehouse_id?: string;
  sync_all_to_warehouse?: SyncAllToWarehouseConfig;
  [key: string]: unknown;
};

/**
 * The shape the browser sees, which is the .NET `webv3api` remapping of the NestJS entity — not the
 * NestJS entity itself. `id` arrives as `connectionId`, enums arrive PascalCased, `lastJobInfo`
 * arrives as a JSON-encoded string, and `lastRefreshedAt` carries a C# `DateTime.MinValue` sentinel
 * rather than a null when a connection has never synced.
 */
export type InventorySyncConnection = {
  connectionId: ConnectionId;
  connectionType: string;
  apiCode: string;
  name: string;
  terminusType: TerminusType;
  supportedTerminusTypes: TerminusType[];
  status: ConnectionStatus;
  externalId: ConnectionExternalId;
  config: ConnectionConfig;
  createdAt: string;
  updatedAt: string;
  lastRefreshedAt: string;
  lastJobInfo: string | null;
};

export type UpdateConnectionRequest = {
  connectionId: ConnectionId;
  terminusType: TerminusType;
  config?: ConnectionConfig;
};

type ConsolidatedInventorySource = {
  connectionId: ConnectionId;
  connectionName: string;
  connectionType: string;
};

export type ConsolidatedInventoryConfig = {
  includedInventorySourceIds?: string[];
  inventorySources?: ConsolidatedInventorySource[];
};

type InventorySourceWarehouse = {
  inventorySourceExternalId: string;
  inventorySourceGuid: string;
  name: string;
};

/**
 * A `GET /inventorysources` entry, which is a different shape from `ConsolidatedInventorySource`: it
 * carries the marketplace warehouses a target connection can push to.
 */
export type InventorySource = {
  inventorySourceId: string;
  inventorySourceExternalId: string;
  name: string;
  warehouses?: InventorySourceWarehouse[];
};
